/**
 * Runtime loader for the BT Assistant "sidekick" web component (support-console host).
 *
 * Mirrors `bluetitan-ui2/src/app/services/assistant-loader.ts` (and tms's console
 * loader): instead of bundling the widget at build time, the console fetches a
 * short-lived minted SAS URL for the published `sidekick.js` bundle, injects it as
 * a `<script>`, and mounts the framework-agnostic `<bt-sidekick>` custom element
 * (`window.BTSidekick.mount`). A widget release then lands on the next page load
 * with no console redeploy.
 *
 * Brain routing: the console authenticates support staff against the corporate
 * Entra tenant for its OWN `bluetitan-support` ticket API (see `environment.
 * apiBaseUrl` + `auth.interceptor.ts`). The assistant, however, is served by
 * **bluetitan-api** -- a DIFFERENT host -- so this loader mints from
 * `${brainBaseUrl}/assistant/bundle-url`, where `brainBaseUrl` is
 * `environment.assistant.brainBaseUrl` (bluetitan-api's base), never
 * `environment.apiBaseUrl` (the support service).
 *
 * Page actions are OFF for the support-staff surface: unlike ui2's superset
 * loader, this interface has NO `actions` option at all -- a call site cannot
 * accidentally wire one in because there is nowhere to put it.
 *
 * Inline per-host loader by design (mirrors ui2/tms): un-versioned, so a
 * shared/bumpable loader package can't reintroduce the per-host release churn
 * this pattern is meant to avoid.
 *
 * Mount-once is enforced at MODULE scope (`mountedElement` below), not by the
 * caller's own instance state -- see that variable's comment for why.
 */

interface BTSidekickApi {
  load: (src: string) => Promise<void>;
  mount: (opts: Record<string, unknown>) => Promise<HTMLElement>;
}

declare global {
  interface Window {
    BTSidekick?: BTSidekickApi;
  }
}

export interface LoadSidekickOpts {
  /** Brain base that serves the mint -- bluetitan-api's base, NOT the support API. */
  brainBaseUrl: string;
  /** Bundle channel: `dev` in dev, `stable` in prod. */
  channel: string;
  /** Element (or selector) the `<bt-sidekick>` mounts into. */
  target: string | Element;
  /** Chat API base the widget talks to (same bluetitan-api base as brainBaseUrl). */
  apiBaseUrl: string;
  /** Widget surface identifier (`support-staff`). */
  surface: string;
  /** Host-owned silent token acquisition (console MSAL -> bearer, or null). */
  getAccessToken: () => Promise<string | null>;
  /** Page-context provider (focused ticket id today; F2 may extend it). */
  getPageContext?: () => unknown;
  /** Extra headers merged onto every chat request. */
  extraHeaders?: () => Record<string, string>;
  /** Show the element's built-in floating launcher button. */
  showLauncher?: boolean;
  /** Show the element's built-in composer. */
  embedComposer?: boolean;
  /** Signed-in user's initials (e.g. "KE") for the user message avatar — the
   *  element can't take an Angular template, so identity is supplied as strings. */
  userInitials?: string;
  /** Signed-in user's avatar photo URL (optional; preferred over initials). */
  userAvatarUrl?: string;
}

const SCRIPT_MARKER = 'data-bt-sidekick-loader';

// Cache the in-flight/settled load PROMISE -- NOT merely "a tag exists". A second
// concurrent caller must await the SAME promise that resolves on the script's real
// `onload`; resolving immediately on tag-presence would let a caller race ahead of
// the still-executing bundle to a not-yet-installed `window.BTSidekick`.
let bundlePromise: Promise<void> | null = null;

// Mount-once guard, deliberately MODULE-scoped rather than left to the caller's own
// instance state. `loadSidekick`'s `target` for this host is `document.body` (an
// app-wide floating launcher), which survives component teardown -- but
// `SidekickPanelComponent` does NOT: `app.component.ts` destroys/recreates it every
// time its `bare()` signal flips (reactive on every NavigationEnd, e.g. an authed
// agent revisiting `/callback` via back/forward). A fresh instance re-runs
// `ngOnInit` -> `mount()` with a reset instance-level guard, so a per-instance
// "loaded" flag is not enough to keep `<bt-sidekick>` mounted exactly once.
// `injectBundle` already no-ops a second `<script>` load via `bundlePromise`, but
// nothing previously stopped a second `window.BTSidekick.mount(...)` call from
// landing a second `<bt-sidekick>` in `document.body` (F1 review, fix round 1).
let mountedElement: Promise<HTMLElement> | null = null;

/**
 * Test-only escape hatch: clears both module-level memoizations so each spec
 * gets a clean slate. Never called from production code.
 */
export function __resetSidekickLoaderForTests(): void {
  bundlePromise = null;
  mountedElement = null;
}

/**
 * Show/hide the app-wide `<bt-sidekick>` WITHOUT unmounting it. The widget mounts
 * ONCE into `document.body` and deliberately survives `SidekickPanelComponent`'s
 * destroy/recreate on a `bare()`<->authed flip (that is the whole point of the
 * module-scoped `mountedElement` guard). But on a bare route (`/intake`,
 * `/callback`) the panel component is gone and the launcher/chat must not remain
 * visible over the anonymous page. Toggling `display` hides it there while keeping
 * the single mount intact, so returning to an authed route shows the SAME element
 * with no re-mint/re-mount (codex-review, 2026-08-11). No-op until something is
 * mounted; a failed mount left no element to toggle.
 */
export function setSidekickVisible(visible: boolean): void {
  if (!mountedElement) return;
  mountedElement
    .then((el) => {
      el.style.display = visible ? '' : 'none';
    })
    .catch(() => {
      /* mount rejected — nothing to show/hide */
    });
}

/**
 * Bound the script-injection trust boundary: the bundle `<script src>` must be an
 * HTTPS Azure Blob URL (the minted SAS points at the bundle storage account), so a
 * tampered/misrouted mint response can't become arbitrary-origin script execution.
 */
function assertTrustedBundleUrl(url: string): void {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    throw new Error('assistant: mint returned a non-URL bundle src');
  }
  if (u.protocol !== 'https:' || !u.hostname.endsWith('.blob.core.windows.net')) {
    throw new Error(`assistant: refusing to load bundle from untrusted origin ${u.origin}`);
  }
}

/**
 * Inject the minted bundle `<script>` ONCE and resolve when it has evaluated.
 * The tag is injected directly (not via `window.BTSidekick.load`) because
 * `window.BTSidekick` is installed *by* the bundle -- it doesn't exist until then.
 */
function injectBundle(src: string): Promise<void> {
  if (bundlePromise) return bundlePromise;
  if (window.BTSidekick && document.querySelector(`script[${SCRIPT_MARKER}]`)) {
    bundlePromise = Promise.resolve();
    return bundlePromise;
  }
  bundlePromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.setAttribute(SCRIPT_MARKER, 'true');
    s.onload = () => resolve();
    s.onerror = () => {
      bundlePromise = null; // allow a retry after a failed load
      s.remove(); // don't leave a dead tag behind (a retry would append a second)
      reject(new Error(`assistant: failed to load bundle ${src}`));
    };
    document.head.appendChild(s);
  });
  return bundlePromise;
}

/**
 * Fetch a minted bundle SAS from bluetitan-api, load the bundle, and mount
 * `<bt-sidekick>` -- but only ONCE for the lifetime of the page (see
 * `mountedElement` above). A caller invoked again (e.g. a recreated host
 * component instance) gets back the SAME settled element without a second
 * mint/mount round-trip. A null token is allowed on the first mount (the API
 * 401s server-side and the widget degrades); a failed mint/HTTP error, an
 * untrusted URL, or a missing `BTSidekick` global throws -- and clears the
 * guard so the NEXT call gets a genuine retry rather than a poisoned no-op.
 */
export async function loadSidekick(opts: LoadSidekickOpts): Promise<HTMLElement> {
  if (mountedElement) return mountedElement;
  mountedElement = mountSidekickOnce(opts).catch((err) => {
    mountedElement = null;
    throw err;
  });
  return mountedElement;
}

async function mountSidekickOnce(opts: LoadSidekickOpts): Promise<HTMLElement> {
  const token = await opts.getAccessToken();
  const res = await fetch(
    `${opts.brainBaseUrl}/assistant/bundle-url?channel=${encodeURIComponent(opts.channel)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    throw new Error(`assistant: bundle-url mint failed (${res.status})`);
  }
  const { url } = (await res.json()) as { url: string };
  assertTrustedBundleUrl(url);
  await injectBundle(url);
  if (!window.BTSidekick) {
    throw new Error('assistant: BTSidekick global not present after bundle load');
  }
  // `mount` internally awaits the async Angular bootstrap (customElements.define),
  // so this is race-free even though the script's onload fires at eval time.
  // Deliberately no `actions` key exists to set here -- page actions are OFF for
  // support-staff (see the module doc comment above).
  const mountOpts: Record<string, unknown> = {
    target: opts.target,
    apiBaseUrl: opts.apiBaseUrl,
    surface: opts.surface,
    transport: 'sse-v1',
    getAccessToken: opts.getAccessToken,
  };
  if (opts.getPageContext !== undefined) mountOpts['getPageContext'] = opts.getPageContext;
  if (opts.extraHeaders !== undefined) mountOpts['extraHeaders'] = opts.extraHeaders;
  if (opts.showLauncher !== undefined) mountOpts['showLauncher'] = opts.showLauncher;
  if (opts.embedComposer !== undefined) mountOpts['embedComposer'] = opts.embedComposer;
  if (opts.userInitials !== undefined) mountOpts['userInitials'] = opts.userInitials;
  if (opts.userAvatarUrl !== undefined) mountOpts['userAvatarUrl'] = opts.userAvatarUrl;
  return window.BTSidekick.mount(mountOpts);
}
